import { intranetNewsPath, type IntranetFeedItem } from '@gb-ticket/shared-types';
import { Loader2Icon, PlusIcon } from 'lucide-react';
import { type ReactNode, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router';

import { cn } from '@/lib/utils';
import { useVoice } from '@/platform/voice/use-voice';
import {
  ColleagueNameButton,
  colleagueVisibleName,
} from '@/products/intranet/components/colleague-name-button';
import { ColleaguePortrait } from '@/products/intranet/features/intranet/colleague-portrait';
import { IntranetAsk } from '@/products/intranet/components/intranet-ask';
import { IntranetEngagementBar } from '@/products/intranet/components/intranet-engagement-bar';
import { IntranetFeedPoll } from '@/products/intranet/components/intranet-feed-poll';
import { IntranetFeedSearchToolbar } from '@/products/intranet/components/intranet-feed-search';
import { IntranetImageFrame } from '@/products/intranet/components/intranet-image-frame';
import { coverSrc } from '@/products/intranet/features/intranet/cover-src';
import { formatIntranetFeedTime } from '@/products/intranet/features/intranet/feed-time';
import {
  effectiveIntranetFeedView,
  INTRANET_FEED_GRID_LAYOUT_QUERY,
  readIntranetFeedView,
  writeIntranetFeedView,
  type IntranetFeedView,
} from '@/products/intranet/features/intranet/feed-view';
import { IntranetHighlightStrip } from '@/products/intranet/components/intranet-highlight-strip';
import { IntranetContentMenu } from '@/products/intranet/components/intranet-content-menu';

/** Fixed cover slot height in raster view so cards align in the grid. */
const GRID_COVER_CLASS = 'aspect-[16/9] shrink-0 overflow-hidden bg-muted/30';

export function IntranetFeedColumn({
  pinned,
  stream,
  pending,
  error,
  writeHref,
  saved = false,
  hasMore = false,
  loadingMore = false,
  loadMoreError = false,
  onLoadMore,
}: {
  pinned: readonly IntranetFeedItem[];
  stream: readonly IntranetFeedItem[];
  pending: boolean;
  error: boolean;
  writeHref: string | null;
  /** Merkliste: other empty copy than an empty rubric. */
  saved?: boolean;
  hasMore?: boolean;
  loadingMore?: boolean;
  loadMoreError?: boolean;
  onLoadMore?: () => void;
}) {
  const [view, setView] = useState<IntranetFeedView>(() =>
    readIntranetFeedView(typeof localStorage === 'undefined' ? null : localStorage),
  );
  const [params] = useSearchParams();
  const searchQuery = params.get('q') ?? '';
  const gridLayoutAvailable = useIntranetFeedGridLayoutAvailable();
  const displayView = effectiveIntranetFeedView(view, gridLayoutAvailable);
  const empty = !pending && !error && pinned.length === 0 && stream.length === 0;
  const sentinelRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const node = sentinelRef.current;
    if (!node || !hasMore || !onLoadMore || loadingMore) {
      return;
    }
    const observer = new IntersectionObserver((entries) => {
      if (entries.some((entry) => entry.isIntersecting)) {
        onLoadMore();
      }
    });
    observer.observe(node);
    return () => observer.disconnect();
  }, [hasMore, loadingMore, onLoadMore]);

  function selectView(next: IntranetFeedView) {
    setView(next);
    writeIntranetFeedView(typeof localStorage === 'undefined' ? null : localStorage, next);
  }

  return (
    <section aria-label="Beiträge" className="min-h-80 space-y-4">
      <FeedToolbar
        view={view}
        onView={selectView}
        writeHref={writeHref}
        showViewToggle={gridLayoutAvailable}
        searchQuery={searchQuery}
      />
      {pending ? (
        <p className="flex min-h-40 items-center gap-2 text-sm text-muted-foreground">
          <Loader2Icon className="size-4 animate-spin" aria-hidden />
          Beiträge werden geladen …
        </p>
      ) : error ? (
        <p role="alert" className="text-sm text-destructive">
          Die Beiträge konnten nicht geladen werden.
        </p>
      ) : (
        <>
          {pinned.length > 0 ? (
            <IntranetHighlightStrip items={pinned} desktop={gridLayoutAvailable} />
          ) : null}
          {stream.length > 0 ? (
            <ul
              className={
                displayView === 'grid'
                  ? 'grid items-stretch gap-4 md:grid-cols-2 xl:grid-cols-3'
                  : 'space-y-4'
              }
            >
              {stream.map((item) => (
                <li key={item.id} className={displayView === 'grid' ? 'h-full' : undefined}>
                  <StreamCard item={item} view={displayView} />
                </li>
              ))}
            </ul>
          ) : null}
          {hasMore ? <div ref={sentinelRef} className="h-px" aria-hidden /> : null}
          {loadingMore ? (
            <p className="flex items-center gap-2 text-sm text-muted-foreground">
              <Loader2Icon className="size-4 animate-spin" aria-hidden />
              Weitere Beiträge werden geladen …
            </p>
          ) : null}
          {loadMoreError ? (
            <p className="text-sm text-destructive">
              Weitere Beiträge konnten nicht geladen werden.{' '}
              <button type="button" className="underline" onClick={onLoadMore}>
                Erneut versuchen
              </button>
            </p>
          ) : null}
          {empty ? (
            <p className="text-sm text-muted-foreground">
              {saved ? 'Noch keine gespeicherten Beiträge.' : 'Keine Beiträge in diesem Channel.'}
            </p>
          ) : null}
        </>
      )}
    </section>
  );
}

function useIntranetFeedGridLayoutAvailable(): boolean {
  const [available, setAvailable] = useState(() => readIntranetFeedGridLayoutAvailable());
  useLayoutEffect(() => {
    if (typeof window.matchMedia !== 'function') {
      return;
    }
    const media = window.matchMedia(INTRANET_FEED_GRID_LAYOUT_QUERY);
    const sync = () => setAvailable(media.matches);
    sync();
    media.addEventListener('change', sync);
    return () => media.removeEventListener('change', sync);
  }, []);
  return available;
}

function readIntranetFeedGridLayoutAvailable(): boolean {
  if (typeof window === 'undefined') {
    return false;
  }
  if (typeof window.matchMedia !== 'function') {
    return true;
  }
  return window.matchMedia(INTRANET_FEED_GRID_LAYOUT_QUERY).matches;
}

function FeedToolbar({
  view,
  onView,
  writeHref,
  showViewToggle,
  searchQuery,
}: {
  view: IntranetFeedView;
  onView: (view: IntranetFeedView) => void;
  writeHref: string | null;
  showViewToggle: boolean;
  searchQuery: string;
}) {
  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-2">
        <div className="hidden min-w-40 flex-1 lg:block">
          <IntranetFeedSearchToolbar initialQuery={searchQuery} />
        </div>
        {showViewToggle ? (
          <div className="flex overflow-hidden rounded-lg border" role="group" aria-label="Ansicht">
            <ViewButton pressed={view === 'list'} onClick={() => onView('list')}>
              Liste
            </ViewButton>
            <ViewButton pressed={view === 'grid'} onClick={() => onView('grid')}>
              Raster
            </ViewButton>
          </div>
        ) : null}
        {writeHref ? (
          <Link
            to={writeHref}
            className="hidden min-h-11 items-center gap-1 rounded-md bg-brand-red px-3 text-sm font-medium text-white focus-visible:ring-2 focus-visible:ring-brand-navy focus-visible:outline-none lg:inline-flex"
          >
            <PlusIcon className="size-4" aria-hidden />
            Beitrag schreiben
          </Link>
        ) : null}
      </div>
      <IntranetAsk variant="home" />
    </div>
  );
}

function ViewButton({
  pressed,
  onClick,
  children,
}: {
  pressed: boolean;
  onClick: () => void;
  children: string;
}) {
  return (
    <button
      type="button"
      aria-pressed={pressed}
      onClick={onClick}
      className={cn(
        'min-h-11 px-3 text-sm font-medium focus-visible:ring-2 focus-visible:ring-brand-navy focus-visible:outline-none',
        pressed ? 'bg-brand-navy text-white' : 'text-foreground hover:bg-muted',
      )}
    >
      {children}
    </button>
  );
}

export function StreamCard({ item, view }: { item: IntranetFeedItem; view: IntranetFeedView }) {
  return (
    <StreamCardFrame
      item={item}
      view={view}
      src={coverSrc(item)}
      coverTo={intranetNewsPath(item.slug)}
      menu={<IntranetContentMenu item={item} />}
      footer={<IntranetEngagementBar item={item} />}
    >
      <div
        className={cn(
          'space-y-1 px-4 py-3',
          view === 'grid' && 'flex min-h-[7.5rem] flex-1 flex-col',
        )}
      >
        <Link
          to={intranetNewsPath(item.slug)}
          className="group block transition-colors hover:bg-muted/40 focus-visible:ring-2 focus-visible:ring-brand-navy focus-visible:outline-none"
        >
          <h3
            className={cn(
              'font-semibold text-primary underline-offset-4 group-hover:underline',
              view === 'grid' && 'line-clamp-2',
            )}
          >
            {item.title}
          </h3>
        </Link>
        <StreamCardText item={item} view={view} />
      </div>
      {view === 'list' ? <IntranetFeedPoll slug={item.slug} allowVote={!item.isDraft} /> : null}
    </StreamCardFrame>
  );
}

/** Card layout shared by the feed and the composer preview. `children` wraps the title and teaser. */
export function StreamCardFrame({
  item,
  view,
  src,
  coverTo,
  channel,
  menu,
  footer,
  children,
}: {
  item: IntranetFeedItem;
  view: IntranetFeedView;
  src: string | null;
  /** When set, cover images (not videos) open this path. */
  coverTo?: string | null;
  channel?: string | null;
  menu?: ReactNode;
  footer: ReactNode;
  children: ReactNode;
}) {
  const voice = useVoice();
  const time = formatIntranetFeedTime(item.publishedAt);
  const cover =
    src !== null ? (
      <CoverMedia item={item} src={src} href={coverTo ?? null} title={item.title} />
    ) : null;

  return (
    <article
      className={cn(
        'overflow-hidden rounded-xl border border-border bg-card shadow-sm',
        view === 'grid' && 'flex h-full flex-col',
      )}
    >
      {view === 'grid' ? (
        <GridCoverSlot item={item} src={src} href={coverTo ?? null} title={item.title} />
      ) : null}
      <div className="flex shrink-0 items-center gap-2 px-4 py-3 text-sm">
        <ColleagueNameButton
          userId={item.createdBy.id}
          displayName={item.createdBy.displayName}
          className="flex min-w-0 items-center gap-2 text-left hover:no-underline"
        >
          <ColleaguePortrait
            displayName={item.createdBy.displayName}
            profilePhotoUrl={item.createdBy.profilePhotoUrl ?? null}
            className="size-8 text-xs ring-offset-background transition-[box-shadow] group-hover:ring-2 group-hover:ring-brand-navy/40"
          />
          <span className="min-w-0 truncate font-medium underline-offset-4 group-hover:text-primary group-hover:underline">
            {colleagueVisibleName(item.createdBy.displayName, voice)}
          </span>
        </ColleagueNameButton>
        {channel ? (
          <span className="min-w-0 truncate text-muted-foreground">· {channel}</span>
        ) : null}
        {time.label ? (
          <time
            className="ml-auto shrink-0 text-muted-foreground"
            dateTime={item.publishedAt ?? undefined}
            title={time.absolute}
          >
            {time.label}
          </time>
        ) : (
          <span className="ml-auto shrink-0 text-muted-foreground">Entwurf</span>
        )}
        {menu}
      </div>
      {view === 'list' ? cover : null}
      {children}
      <div className={view === 'grid' ? 'mt-auto shrink-0' : undefined}>{footer}</div>
    </article>
  );
}

function GridCoverSlot({
  item,
  src,
  href,
  title,
}: {
  item: IntranetFeedItem;
  src: string | null;
  href: string | null;
  title?: string;
}) {
  return (
    <div className={GRID_COVER_CLASS}>
      {src ? <CoverMedia item={item} src={src} href={href} title={title} fit="cover" /> : null}
    </div>
  );
}

export function StreamCardText({
  item,
  view = 'list',
  onShowMore,
}: {
  item: IntranetFeedItem;
  view?: IntranetFeedView;
  /** Composer preview: switch to detail without submitting the form. */
  onShowMore?: () => void;
}) {
  const grid = view === 'grid';
  return (
    <>
      {item.teaser ? (
        <p
          className={cn(
            'mt-1 text-sm text-muted-foreground',
            grid ? 'line-clamp-3' : 'whitespace-pre-line',
          )}
        >
          {item.teaser}
        </p>
      ) : null}
      {item.teaserHasMore ? (
        onShowMore ? (
          <button
            type="button"
            onClick={onShowMore}
            className="mt-1 text-sm font-medium text-primary underline-offset-4 hover:underline focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-none"
          >
            … Mehr anzeigen
          </button>
        ) : item.slug ? (
          <Link
            to={intranetNewsPath(item.slug)}
            className="mt-1 inline-block text-sm font-medium text-primary underline-offset-4 hover:underline focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-none"
          >
            … Mehr anzeigen
          </Link>
        ) : null
      ) : null}
    </>
  );
}

export function CoverMedia({
  item,
  src,
  href = null,
  title,
  fit = 'natural',
  expandable = false,
}: {
  item: IntranetFeedItem;
  src: string;
  /** Opens the article when the cover is an image. Videos keep native controls. */
  href?: string | null;
  title?: string;
  /** Raster cards crop covers to a fixed slot; list and the article use the 680 preview. */
  fit?: 'natural' | 'cover';
  /** Article pages open the original file. Feed cards keep the link to the article. */
  expandable?: boolean;
}) {
  const openLabel = title ? `Beitrag öffnen: ${title}` : 'Beitrag öffnen';
  if (item.coverMedia === 'video') {
    const className = fit === 'cover' ? 'size-full object-cover' : 'h-auto w-full';
    return <video src={src} controls playsInline className={className} />;
  }
  if (fit === 'cover') {
    const image = <img src={src} alt={item.coverAlt ?? ''} className="size-full object-cover" />;
    if (!href) {
      return image;
    }
    return (
      <Link
        to={href}
        aria-label={openLabel}
        className="block focus-visible:ring-2 focus-visible:ring-brand-navy focus-visible:outline-none"
      >
        {image}
      </Link>
    );
  }
  const framed = (
    <IntranetImageFrame src={src} alt={item.coverAlt ?? ''} expandable={expandable && !href} />
  );
  if (!href) {
    return framed;
  }
  return (
    <Link
      to={href}
      aria-label={openLabel}
      className="block focus-visible:ring-2 focus-visible:ring-brand-navy focus-visible:outline-none"
    >
      {framed}
    </Link>
  );
}
